import {
  Building2,
  Calculator,
  CircleCheck,
  FileAudio,
  FileImage,
  FileVideo2,
  Grid2X2,
  SquareUserRound,
  Tags,
} from "lucide-react";
import { NavLink, useLocation } from "react-router";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { isLegitimateOrg } from "@/data/auth";
import { WorkOSSessionRole } from "@/data/types";
import { useAppStatus } from "@/hooks/use-banner-data";
import { cn } from "@/lib/utils";
import useStore from "@/state";

import { NavUser } from "./nav-user";
import { OrganizationSwitcher } from "./organization-switcher";

const tagLinks = [
  {
    title: "SKUs",
    url: "/skus",
    icon: Grid2X2,
  },
  {
    title: "Tags",
    url: "/tags",
    icon: Tags,
  },
  {
    title: "Verifications",
    url: "/verifications",
    icon: CircleCheck,
  },
  {
    title: "Owners",
    url: "/owners",
    icon: SquareUserRound,
  },
];

const assetLinks = [
  {
    title: "Images",
    url: "/images",
    icon: FileImage,
  },
  {
    title: "Videos",
    url: "/videos",
    icon: FileVideo2,
  },
  {
    title: "Tracks",
    url: "/tracks",
    icon: FileAudio,
  },
];

const adminLinks = [
  {
    title: "Organizations",
    url: "/organizations",
    icon: Building2,
  },
  {
    title: "Gas Cost Calculator",
    url: "/gas-calculator",
    icon: Calculator,
  },
];

export const AppSidebar = () => {
  const location = useLocation();
  const { showBanner } = useAppStatus();
  const { session } = useStore();

  if (!session) {
    return null;
  }

  return (
    <Sidebar collapsible="icon" className={cn(showBanner && "pt-8")}>
      <SidebarHeader>
        <OrganizationSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Platform</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {tagLinks.map((link) => (
                <SidebarMenuItem key={link.title}>
                  <SidebarMenuButton
                    isActive={location.pathname.startsWith(link.url)}
                    asChild
                  >
                    <NavLink to={link.url}>
                      <link.icon />
                      <span>{link.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Assets</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {assetLinks.map((link) => (
                <SidebarMenuItem key={link.title}>
                  <SidebarMenuButton
                    isActive={location.pathname.startsWith(link.url)}
                    asChild
                  >
                    <NavLink to={link.url}>
                      <link.icon />
                      <span>{link.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {session.role === WorkOSSessionRole.ADMIN &&
          isLegitimateOrg(session.organization_id) && (
            <SidebarGroup>
              <SidebarGroupLabel>Internal</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminLinks.map((link) => (
                    <SidebarMenuItem key={link.title}>
                      <SidebarMenuButton
                        isActive={location.pathname.startsWith(link.url)}
                        asChild
                      >
                        <NavLink to={link.url}>
                          <link.icon />
                          <span>{link.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
};
